/**
 * Bynder SDK for file uploads and asset management
 * Ported from loyalty-admin-ui
 */
interface BynderToken {
  expired: () => boolean
  refresh: () => Promise<BynderToken>
  token: {
    access_token: string
  }
}

interface BynderOptions {
  baseURL: string
  token?: BynderToken
  permanentToken?: string
}

type UploadBody = Blob | File | {read?: () => unknown; size?: number}

interface BynderInitResponse {
  s3file: {
    uploadid: string
    targetid: string
  }
  s3_filename: string
  multipart_params: Record<string, string>
}

interface ChunkUploadResult {
  init: BynderInitResponse
  chunkNumber: number
}

type UrlSearchParamValue = string | number | boolean

/**
 * Rejects the request.
 * @return {Promise} error - Returns a Promise with the details for the wrong request.
 */
function rejectValidation(module: string, param: string) {
  return Promise.reject({
    status: 0,
    message: `The ${module} ${param} is not valid or it was not specified properly`,
  })
}

/**
 * @classdesc Represents an API call.
 * @class
 * @abstract
 */
class APICall {
  baseURL: string
  token?: BynderToken
  permanentToken?: string

  constructor(baseURL: string, token?: BynderToken) {
    try {
      new URL(baseURL)
    } catch {
      throw new Error('The base URL provided is not valid')
    }

    this.baseURL = baseURL
    this.token = token
  }

  async send(
    method: string,
    url: string,
    controller?: AbortController,
    data: Record<string, UrlSearchParamValue> = {}
  ) {
    let callURL = new URL(url, this.baseURL).href

    if (!this.token && !this.permanentToken) {
      throw new Error('No token found')
    }

    const headers: Record<string, string> = {
      'User-Agent': 'bynder-js-sdk/2.3.9',
    }

    if (this.permanentToken) {
      headers['Authorization'] = 'Bearer ' + this.permanentToken
    } else if (this.token) {
      const refreshedToken = this.token.expired() ? await this.token.refresh() : this.token
      this.token = refreshedToken
      headers['Authorization'] = 'Bearer ' + this.token.token.access_token
    }

    let body: string | undefined

    if (method === 'POST') {
      headers['Content-Type'] = 'application/x-www-form-urlencoded'
      body = new URLSearchParams(data as Record<string, string>).toString()
    } else if (Object.keys(data).length && data.constructor === Object) {
      callURL = new URL(url + '?' + new URLSearchParams(data as Record<string, string>).toString(), this.baseURL).href
    }

    try {
      const response = await fetch(callURL, {
        method,
        headers,
        body,
        mode: 'cors',
        signal: controller?.signal,
      })
      return response.json()
    } catch (error) {
      console.error('fetch', error)
      return Promise.reject(error)
    }
  }

  async sendNoAuth(
    method: string,
    url: string,
    controller?: AbortController,
    data: Record<string, UrlSearchParamValue> = {}
  ) {
    let callURL = new URL(url, this.baseURL).href

    let body: string | undefined

    const headers: Record<string, string> = {
      'User-Agent': 'bynder-js-sdk/2.3.9',
    }

    if (method === 'POST') {
      headers['Content-Type'] = 'application/x-www-form-urlencoded'
      body = new URLSearchParams(data as Record<string, string>).toString()
    } else if (Object.keys(data).length && data.constructor === Object) {
      callURL = new URL(url + '?' + new URLSearchParams(data as Record<string, string>).toString(), this.baseURL).href
    }

    const response = await fetch(callURL, {
      method,
      headers,
      body,
      mode: 'cors',
      signal: controller?.signal,
    })
    return response.json()
  }
}

const bodyTypes = {
  BLOB: 'BLOB',
  STREAM: 'STREAM',
  get: (body: UploadBody): string | null => {
    if (typeof window !== 'undefined' && window.Blob && body instanceof window.Blob) {
      return bodyTypes.BLOB
    }
    if ('read' in body && typeof body.read === 'function') {
      return bodyTypes.STREAM
    }
    return null
  },
}

function getLength(file: {body: UploadBody; length?: number}): number {
  const {body, length} = file
  const bodyType = bodyTypes.get(body)
  if (bodyType === bodyTypes.BLOB) {
    return (body as Blob).size
  }
  return length || 0
}

interface UploadFile {
  filename: string
  body: Blob | File
  length?: number
  data: Record<string, UrlSearchParamValue>
  additional?: boolean
}

interface ProgressCallback {
  (state: {action: string; completed?: string; chunks?: number; chunksUploaded: number}): void
}

/**
 * @classdesc Represents the Bynder SDK. It allows the user to make every call to the API with a single function.
 * @class
 */
class Bynder {
  baseURL: string
  api: APICall

  constructor(options: BynderOptions) {
    this.baseURL = options.baseURL
    this.api = new APICall(options.baseURL, options.token)

    if (typeof options.permanentToken === 'string') {
      this.api.permanentToken = options.permanentToken
    }
  }

  getClosestUploadEndpoint(controller?: AbortController) {
    return this.api.sendNoAuth('GET', 'upload/endpoint', controller)
  }

  initUpload(filename: string, controller?: AbortController) {
    if (!filename) {
      return rejectValidation('upload', 'filename')
    }
    return this.api.sendNoAuth('POST', 'upload/init', controller, {filename})
  }

  registerChunk(init: BynderInitResponse, chunkNumber: number, controller?: AbortController) {
    const {s3file, s3_filename: filename} = init
    const {uploadid, targetid} = s3file
    return this.api.send('POST', 'v4/upload/', controller, {
      id: uploadid,
      targetid,
      filename: `${filename}/p${chunkNumber}`,
      chunkNumber,
    })
  }

  finaliseUpload(
    init: BynderInitResponse,
    filename: string,
    chunks: number,
    mediaId?: string,
    controller?: AbortController
  ) {
    const {s3file, s3_filename: s3filename} = init
    const {uploadid, targetid} = s3file
    const payload = {
      targetid,
      s3_filename: `${s3filename}/p${chunks}`,
      chunks,
    }
    if (mediaId) {
      const finalizeUrl = `v4/media/${mediaId}/save/additional/${uploadid}`
      return this.api.send('POST', finalizeUrl, controller, payload)
    } else {
      return this.api.send('POST', `v4/upload/${uploadid}/`, controller, {
        ...payload,
        original_filename: filename,
      })
    }
  }

  pollUploadStatus(importIds: string[], controller?: AbortController) {
    return this.api.send('GET', 'v4/upload/poll/', controller, {
      items: importIds.join(','),
    })
  }

  waitForUploadDone(importIds: string[], controller?: AbortController) {
    const POLLING_INTERVAL = 2000
    const MAX_POLLING_ATTEMPTS = 60
    const pollUploadStatus = this.pollUploadStatus.bind(this)
    return new Promise((resolve, reject) => {
      let attempt = 0
      ;(function checkStatus() {
        pollUploadStatus(importIds, controller)
          .then(pollStatus => {
            if (pollStatus !== null) {
              const {itemsDone, itemsFailed} = pollStatus
              if (itemsDone.length === importIds.length) {
                return resolve({itemsDone})
              }
              if (itemsFailed.length > 0) {
                return reject({itemsFailed})
              }
            }
            if (++attempt > MAX_POLLING_ATTEMPTS) {
              return reject(new Error(`Stopped polling after ${attempt} attempts`))
            }
            return setTimeout(checkStatus, POLLING_INTERVAL)
          })
          .catch(reject)
      })()
    })
  }

  saveAsset(data: Record<string, UrlSearchParamValue>, controller?: AbortController) {
    const {brandId, mediaId} = data
    if (!brandId) {
      return rejectValidation('upload', 'brandId')
    }
    const saveURL = mediaId ? `v4/media/${mediaId}/save/` : 'v4/media/save/'

    return this.api.send('POST', saveURL, controller, {
      ...data,
      isPublic: true,
      tags: data.tags || import.meta.env.VITE_PROGRAM,
    })
  }

  uploadFileInChunks(
    file: UploadFile,
    endpoint: string,
    init: BynderInitResponse,
    progressCallback: ProgressCallback,
    controller?: AbortController
  ): Promise<ChunkUploadResult> {
    const {body} = file
    const bodyType = bodyTypes.get(body)
    const length = getLength(file)
    const CHUNK_SIZE = 1024 * 1024 * 5
    const chunks = Math.ceil(length / CHUNK_SIZE)

    const registerChunk = this.registerChunk.bind(this)
    const uploadPath = init.multipart_params.key

    const uploadChunkToS3 = (chunkData: Blob, chunkNumber: number) => {
      const form = new FormData()
      const params = Object.assign(init.multipart_params, {
        name: `${new URL('protocol:' + uploadPath).pathname.split('/').pop()}/p${chunkNumber}`,
        chunk: chunkNumber,
        chunks,
        Filename: `${uploadPath}/p${chunkNumber}`,
        key: `${uploadPath}/p${chunkNumber}`,
      })
      Object.keys(params).forEach(key => {
        form.append(key, params[key])
      })
      form.append('file', chunkData)

      return fetch(endpoint, {
        method: 'POST',
        body: form,
        signal: controller?.signal,
      })
    }

    function delay(ms: number) {
      return new Promise(resolve => {
        setTimeout(resolve, ms)
      })
    }

    progressCallback({
      action: 'Uploading file',
      completed: 'Initializing',
      chunksUploaded: 0,
      chunks,
    })

    function nextChunk(chunkNumber: number): Promise<ChunkUploadResult> {
      if (chunkNumber >= chunks) {
        return Promise.resolve({init, chunkNumber})
      }
      let chunkData: Blob
      if (bodyType === bodyTypes.STREAM) {
        return delay(50).then(() => {
          progressCallback({
            action: 'Uploading file',
            completed: 'Initializing',
            chunksUploaded: chunkNumber,
            chunks,
          })
          return nextChunk(chunkNumber)
        })
      } else {
        const start = chunkNumber * CHUNK_SIZE
        const end = Math.min(start + CHUNK_SIZE, length)
        chunkData = (body as Blob).slice(start, end)
      }
      const newChunkNumber = chunkNumber + 1
      return uploadChunkToS3(chunkData, newChunkNumber)
        .then(() => {
          return registerChunk(init, newChunkNumber, controller)
        })
        .then(() => {
          progressCallback({
            action: 'Uploading file',
            completed: 'Initializing',
            chunksUploaded: chunkNumber,
            chunks,
          })
          return nextChunk(newChunkNumber)
        })
    }
    return nextChunk(0)
  }

  async uploadFile(file: UploadFile, progressCallback: ProgressCallback = () => {}, controller?: AbortController) {
    const {body, filename, data, additional} = file
    const {brandId} = data
    const bodyType = bodyTypes.get(body)
    const length = getLength(file)

    if (!brandId) {
      return rejectValidation('upload', 'brandId')
    }
    if (!filename) {
      return rejectValidation('upload', 'filename')
    }
    if (!body || !bodyType) {
      return rejectValidation('upload', 'body')
    }
    if (!length || typeof length !== 'number') {
      return rejectValidation('upload', 'length')
    }
    if (additional && !data.id) {
      return rejectValidation('upload', 'id')
    }

    const getClosestUploadEndpoint = this.getClosestUploadEndpoint.bind(this)
    const initUpload = this.initUpload.bind(this)
    const uploadFileInChunks = this.uploadFileInChunks.bind(this)
    const finaliseUpload = this.finaliseUpload.bind(this)
    const saveAsset = this.saveAsset.bind(this)
    const waitForUploadDone = this.waitForUploadDone.bind(this)
    let totalChunks: number

    progressCallback({
      action: 'Initializing',
      chunksUploaded: 0,
    })
    return Promise.all([getClosestUploadEndpoint(controller), initUpload(filename, controller)])
      .then(res => {
        const [endpoint, init] = res
        return uploadFileInChunks(file, endpoint, init, progressCallback, controller)
      })
      .then(uploadResponse => {
        const {init, chunkNumber} = uploadResponse
        totalChunks = chunkNumber
        progressCallback({
          action: 'Finalizing upload',
          completed: 'Uploading file',
          chunksUploaded: chunkNumber,
          chunks: chunkNumber,
        })
        return finaliseUpload(init, filename, chunkNumber, additional ? String(data.id) : undefined, controller)
      })
      .then(finalizeResponse => {
        if (additional) {
          return Promise.resolve(finalizeResponse)
        }
        const {importId} = finalizeResponse
        return waitForUploadDone([importId], controller)
      })
      .then(doneResponse => {
        if (additional) {
          return Promise.resolve(doneResponse)
        }
        const {itemsDone} = doneResponse
        const importId = itemsDone[0]
        progressCallback({
          action: 'Saving asset',
          completed: 'Finalizing upload',
          chunksUploaded: totalChunks,
          chunks: totalChunks,
        })
        return saveAsset(Object.assign(data, {importId, isPublic: true}), controller)
      })
  }

  getAssetsByCollection(collectionId: string, controller?: AbortController) {
    if (!collectionId) {
      return rejectValidation('collection', 'id')
    }
    return this.api.send(
      'GET',
      `v4/media/?collectionId=${collectionId}&orderBy=name%20asc&limit=250&page=1&count=1&total=1`,
      controller
    )
  }

  getAssetsByTag(tag: string, controller?: AbortController) {
    if (!tag) {
      return rejectValidation('tag', 'name')
    }
    return this.api.send(
      'GET',
      `v4/media/?tags=${tag}&orderBy=dateCreated%20desc&limit=250&page=1&count=1&total=1`,
      controller
    )
  }

  getMediaInfo({id, ...options}: {id: string; [key: string]: string} = {id: ''}) {
    if (!id) {
      return rejectValidation('media', 'id')
    }

    return this.api.send('GET', `v4/media/${id}/`, undefined, options)
  }

  /**
   * Get the assets total according to the parameters provided.
   * @see {@link http://docs.bynder.apiary.io/#reference/assets/asset-operations/retrieve-assets|API Call}
   * @param {Object} [params={}] - An object containing the parameters accepted by the API to narrow the query.
   * @return {Promise} Number - Returns a Promise that, when fulfilled, will either return the number of assets
   * fitting the query or an Error with the problem.
   */
  getMediaTotal(params: Record<string, UrlSearchParamValue> = {}) {
    const parametersObject = Object.assign({}, params, {count: true})
    return this.api.sendNoAuth('GET', 'v4/media/', undefined, parametersObject).then(data => {
      return data.count.total
    })
  }
}

export default Bynder
